import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { formatPrice } from '@codex-brightworld/helpers';
import { Icon } from '@codex-brightworld/icons';

export const VARIANT_FO_SITE_LISTING_PAGE_LIST = 'listing_list';
export const VARIANT_FO_SITE_LISTING_PAGE_GRID = 'listing_grid';
export const VARIANT_FO_SITE_PRODUCT_PAGE = 'product';
export const VARIANT_FO_SITE_PSA = 'psa';
export const VARIANT_FO_SITE_COMPARE_POPUP = 'compare_popup';
export const VARIANT_FO_SITE_COMPARE_SLIDER = 'compare_slider';

const PriceBlockWrapper = styled.div`
  ${({
    theme,
    variant,
    compact,
    wasPriceLabel
  }) => css`
    padding-top: 9px;
    padding-bottom: 8px;
    font-family: ${theme.fonts.default.fontFamily};

    ${variant === VARIANT_FO_SITE_LISTING_PAGE_LIST && css`
      padding-top: ${wasPriceLabel ? '12px;' : '9px;'}
      padding-bottom: 6px;
      @media ${theme.breakpoints.tablet} {
        padding-bottom: 9px;
      };
      @media ${theme.breakpoints.mobile} {
        padding-bottom: 9px;
      };
    `};
    ${variant === VARIANT_FO_SITE_LISTING_PAGE_GRID && css`
      padding-top: 10px;
      padding-bottom: 10px;
    `};
    ${variant === VARIANT_FO_SITE_PRODUCT_PAGE && css`
      padding-top: 11px;
      width: 100%;
    `};
    ${variant === VARIANT_FO_SITE_PSA && css`
      padding-top: 10px;
      padding-bottom: 7px;
      padding-bottom: ${wasPriceLabel} ? '3px' : '7px';
    `};
    ${variant === VARIANT_FO_SITE_COMPARE_POPUP && css`
      padding-top: 10px;
      padding-bottom: 5px;
    `};
    ${variant === VARIANT_FO_SITE_COMPARE_SLIDER && css`
      width: 210px;
      padding-top: 11.5px;
      padding-bottom: 8.5px;
    `};
    ${compact && css`
      padding-top: 5px;
      padding-bottom: 5px;
      line-height: 12px;
      border-color: #d3d3d3;
    `};
    text-align: center;
  `};
`;

const Price = styled.span`
  ${({
    variant,
    compact,
    theme,
    outOfStock
  }) => css`
    display: inline-block;
    line-height: 1.2;
    font-weight: bold;
    vertical-align: bottom;
    font-size: 20px;
    color: ${outOfStock ? theme.colors.neutral.grey[4] : theme.colors.neutral.currysBlack};

    ${variant === VARIANT_FO_SITE_LISTING_PAGE_GRID && css`
      width: 100px;
      font-size: 22px;
      line-height: 0.73;
      vertical-align: bottom;
    `}
    ${variant === VARIANT_FO_SITE_LISTING_PAGE_LIST && css`
      font-size: 22px;
      line-height: 0.91;
    `}
    ${variant === VARIANT_FO_SITE_PRODUCT_PAGE && css`
      width: 50%;
      font-size: 30px;
      line-height: 0.77;
    `}
    ${variant === VARIANT_FO_SITE_PSA && css`
      width: 103px;
      font-size: 25px;
      line-height: 0.72;
    `}
    ${variant === VARIANT_FO_SITE_COMPARE_POPUP && css`
      width: 144px;
      font-size: 22px;
      line-height: 1.05;
    `}
    ${variant === VARIANT_FO_SITE_COMPARE_SLIDER && css`
      font-size: 22px;
      line-height: 1.05;
    `}
    ${compact && css`
      font-size: 13px;
    `}
  `}
`;

const WebExclusivePriceWrapper = styled.div`
  font-size: 13px;
  font-weight: bold;
  text-align: center;
`;

const PricePerMonthWrapper = styled.span`
  font-size: 14px;
  font-weight: bold;
`;

const SaveLabelDesktop = styled.span`
  ${({
    theme,
    outOfStock,
    variant,
    compact,
    displayWasPrice
  }) => css`
    color: ${outOfStock ? theme.colors.neutral.grey[4] : theme.colors.primary.magenta};
    display: inline-block;
    margin-left: 8px;
    text-align: center;
    font-size: 14px;
    line-height: 21px;
    font-weight: bold;
    vertical-align: bottom;

    ${variant === VARIANT_FO_SITE_LISTING_PAGE_LIST && css`
      width: 100px;
      padding: 3px 0 5px 0;
    `}
    ${variant === VARIANT_FO_SITE_LISTING_PAGE_GRID && css`
      width: 100px;
      padding: 2px 0 4px 0;
    `}
    ${!displayWasPrice && variant === VARIANT_FO_SITE_LISTING_PAGE_GRID && css`
      margin-top: 10px;
    `}
    ${variant === VARIANT_FO_SITE_PRODUCT_PAGE && css`
      font-size: 16px;
      width: 50%;
      padding-left: 0;
      padding-right: 0;
      margin-left: 0;
    `};
    ${variant === VARIANT_FO_SITE_PSA && css`
      font-size: 13px;
      line-height: 1.23;
      padding-top: 1px;
      padding-bottom: 2px;
      margin-left: 0;
    `}
    ${variant === VARIANT_FO_SITE_COMPARE_POPUP && css`
      width: 95px;
    `}
    ${compact && css`
      font-size: 13px;
    `}
    font-weight: bold;
    @media ${theme.breakpoints.tablet} {
      display: none;
    };
    @media ${theme.breakpoints.mobile} {
      display: none;
    };
  `}
`;

const SaveLabelTabletMobile = styled.span`
  ${({
    theme,
    outOfStock,
    variant,
    compact,
    displayWasPrice
  }) => css`
    color: ${outOfStock ? theme.colors.neutral.grey[4] : theme.colors.primary.magenta};
    display: inline-block;
    margin-left: 8px;
    text-align: center;
    font-size: 14px;
    line-height: 21px;
    font-weight: bold;
    vertical-align: bottom;

    ${variant === VARIANT_FO_SITE_LISTING_PAGE_LIST && css`
      width: 100px;
      padding: 3px 0 5px 0;
    `}
    ${variant === VARIANT_FO_SITE_LISTING_PAGE_GRID && css`
      width: 100px;
      padding: 2px 0 4px 0;
    `}
    ${!displayWasPrice && variant === VARIANT_FO_SITE_LISTING_PAGE_GRID && css`
      margin-top: 10px;
    `}
    ${variant === VARIANT_FO_SITE_PRODUCT_PAGE && css`
      font-size: 16px;
      width: 50%;
      padding-left: 0;
      padding-right: 0;
      margin-left: 0;
    `};
    ${variant === VARIANT_FO_SITE_PSA && css`
      font-size: 13px;
      line-height: 1.23;
      padding-top: 1px;
      padding-bottom: 2px;
    `}
    ${compact && css`
      font-size: 13px;
    `}
    font-weight: bold;
    display: none;
    @media ${theme.breakpoints.tablet} {
      display: inline-block;
    };
    @media ${theme.breakpoints.mobile} {
      display: inline-block;
    };
  `}
`;

const WasPrice = styled.div`
  ${({
    theme,
    displayWasPriceRange,
    wasPriceFrom,
    wasPriceTo,
    variant
  }) => css`
    color: ${theme.colors.neutral.grey[4]};
    font-size: 14px;
    line-height: 21px;
    margin-top: 2px;
    ${displayWasPriceRange &&
    wasPriceFrom &&
    wasPriceTo &&
    variant === VARIANT_FO_SITE_LISTING_PAGE_LIST && css`
      @media ${theme.breakpoints.tablet} {
        display: none;
      };
      @media ${theme.breakpoints.mobile} {
        display: none;
      };
    `}
  `}
`;

const WasPriceRange = styled.span`
  font-size: 12px;
`;

const DropLabel = styled.div`
  ${({ theme }) => css`
    height: 20px;
    font-weight: ${theme.fontWeights.bold};
    line-height: 1.7;
    background: #ffefef;
    color: ${theme.colors.primary.magenta};
    text-align: center;
    font-size: 13px;

    ${Icon} {
      margin-top: -6px;
      fill: ${theme.colors.primary.magenta};
      width: 13px;
      height: 13px;
    }
  `}
`;

const BuyTogetherBlock = styled.div`
  text-align: center;
`;

const BuyTogetherLabel = styled.div`
  ${({ theme }) => css`
    font-size: 14px;
    font-weight: bold;
    color: ${theme.colors.primary.purple};
    padding-top: 6px;
  `}
`;

const SeparatePriceLabel = styled.div`
  ${({ theme }) => css`
    font-size: 13px;
    color: ${theme.colors.neutral.currysBlack};
  `}
`;

export const SavePrice = styled(Price)`
  ${({
    block,
    bundle
  }) => css`
    font-size: 14px;
    font-weight: ${bundle ? 'normal' : 'bold'};
    display: ${block ? 'block' : 'inline'};
  `};
`;

const BundlePricesLegacy = styled.div`
  ${({
    theme,
    outOfStock
  }) => css`
    color: ${outOfStock ? theme.colors.neutral.grey[4] : theme.colors.neutral.currysBlack};
    font-size: 12px;
    line-height: 14px;

    ${SavePrice} {
      color: ${outOfStock ? theme.colors.neutral.grey[4] : theme.colors.neutral.currysBlack};
      font-size: 12px;
      line-height: 14px;
    };
  `};
`;

const Prices = styled.div`
  &, ${SavePrice} {
    font-size: 12px;
  }
`;

const BundlePrices = styled.div`
${({ theme }) => css`
    margin-top: 5px;
    margin-bottom: 5px;
    color: ${theme.colors.neutral.currysBlack};
    font-size: 12px;
    text-align: center;
  `}
`;

const InStorePrice = styled.p`
  font-size: 13px;
  margin-top: 5px;
  margin-bottom: 15px;
`;

const ProductPriceBlock = (props) => {

  let priceLabel = formatPrice(props.currentPrice, false, props.currency);
  const pricePerMonthLabel = props.perMonthPrice ? ` ${props.perMonthTranslation}` : '';

  let isBuyTogether = false;
  let separatePrice = '';
  if (props.buyTogetherPrice) {
    isBuyTogether = true;
    priceLabel = formatPrice(props.buyTogetherPrice, false, props.currency);
    separatePrice = formatPrice(props.currentPrice, false, props.currency);
  }

  let saveLabelDesktop = '';
  let saveLabelTabletMobile = '';
  let wasPriceLabel = '';
  let wasPriceRange = '';
  let formattedWasPrice = '';
  if (props.wasPrice) {
    formattedWasPrice = formatPrice(props.wasPrice, false, props.currency);
  }
  let formattedSavingsPrice = '';
  if (props.savingsPrice) {
    formattedSavingsPrice = formatPrice(props.savingsPrice, false, props.currency);
    saveLabelDesktop = (
      <SaveLabelDesktop
        outOfStock={props.outOfStock}
        variant={props.variant}
        compact={props.compact}
        displayWasPrice={props.displayWasPrice}
      >
        <span>{formattedSavingsPrice}</span>
      </SaveLabelDesktop>
    );
    saveLabelTabletMobile = (
      <SaveLabelTabletMobile
        outOfStock={props.outOfStock}
        variant={props.variant}
        compact={props.compact}
        displayWasPrice={props.displayWasPrice}
      >
        <span>{formattedSavingsPrice}</span>
      </SaveLabelTabletMobile>
    );
    if (props.displayWasPriceRange && props.wasPriceFrom && props.wasPriceTo) {
      const from = `${props.fromDateTranslation
        ? `${props.fromDateTranslation} `
        : ''}${props.wasPriceFrom}`;
      const to = `${props.toDateTranslation ? props.toDateTranslation : '-'} ${props.wasPriceTo}`;
      wasPriceRange = ` (${from} ${to})`;
    }
    wasPriceLabel = (
      <>
        {formattedWasPrice}
        <WasPriceRange>
          {wasPriceRange}
        </WasPriceRange>
      </>
    );
  }

  const getPricesOutputForNonBundleProduct = () => (
    <>
      {(props.displayWasPriceRange || props.compact) && saveLabelDesktop}
      {props.variant !== VARIANT_FO_SITE_LISTING_PAGE_GRID && saveLabelTabletMobile}
      {props.displayWasPrice && wasPriceLabel && !props.compact && (
      <WasPrice
        displayWasPriceRange={props.displayWasPriceRange}
        wasPriceFrom={props.wasPriceFrom}
        wasPriceTo={props.wasPriceTo}
        variant={props.variant}
      >
        {wasPriceLabel}
        {!props.displayWasPriceRange && props.displayWasPrice && saveLabelDesktop}
        {!props.displayWasPriceRange && props.displayWasPrice && saveLabelTabletMobile}
      </WasPrice>
      )}
      {!props.displayWasPrice && props.variant === VARIANT_FO_SITE_LISTING_PAGE_GRID &&
        saveLabelDesktop}
      {!props.displayWasPrice && props.variant === VARIANT_FO_SITE_LISTING_PAGE_GRID &&
        saveLabelTabletMobile}
    </>
  );

  const getPricesOutputForBundleProduct = () => (
    <>
      {props.bundle && (
        <BundlePricesLegacy outOfStock={props.outOfStock}>
          {props.mainProductWasPrice && (
            <div>
              Main product was {formatPrice(props.mainProductWasPrice, false)}
            </div>
          )}
          {props.mainProductWasPriceFrom && props.mainProductWasPriceTo && (
            <div>
              (from {props.mainProductWasPriceFrom} to {props.mainProductWasPriceTo}),
            </div>
          )}
          {(props.mainProductCurrentPrice || props.mainProductSaving) && (
            <Prices>
              {props.mainProductCurrentPrice && (
                <span>
                  {`now ${formatPrice(props.mainProductCurrentPrice, false)}. `}
                </span>
              )}
              {props.mainProductSaving && (
                <SavePrice outOfStock={props.outOfStock} bundle={props.bundle}>
                  Save {formatPrice(props.mainProductSaving, false)}
                </SavePrice>
              )}
            </Prices>
          )}
        </BundlePricesLegacy>
      )}
      {(props.mainProductPriceLabel || props.separateSellingPriceLabel) &&
      props.displayBundlePriceInsidePriceBlock && (
        <BundlePrices>
          {props.mainProductPriceLabel}
          {`${props.mainProductPriceLabel ? '\n' : ''}`}
          {props.separateSellingPriceLabel}
        </BundlePrices>
      )}
    </>
  );

  return (
    <>
      {props.priceDropped && (
        <DropLabel>
          <Icon icon="price_promise_3" />&nbsp;{props.priceDropLabel}
        </DropLabel>
      )}
      <PriceBlockWrapper
        href={props.priceDropLink}
        onClick={props.priceDropLink ? props.onPriceDropLinkClicked : undefined}
        data-qa={props.qa}
        data-interaction={props.interaction}
        outOfStock={props.outOfStock}
        className={props.className}
        variant={props.variant}
        compact={props.compact}
        wasPriceLabel={wasPriceLabel}
      >
        {props.isFlashSale && props.webExclusivePriceTranslation && (
          <WebExclusivePriceWrapper>
            {props.webExclusivePriceTranslation}
          </WebExclusivePriceWrapper>
        )}
        <Price
          variant={props.variant}
          compact={props.compact}
          outOfStock={props.outOfStock}
        >
          {priceLabel}
          <PricePerMonthWrapper>
            {pricePerMonthLabel}
          </PricePerMonthWrapper>
        </Price>
        {isBuyTogether && (
          <BuyTogetherBlock>
            <BuyTogetherLabel>Buy together and save</BuyTogetherLabel>
            <SeparatePriceLabel>Separate price {separatePrice}</SeparatePriceLabel>
          </BuyTogetherBlock>
        )}
        {(props.bundle || props.mainProductPriceLabel || props.separateSellingPriceLabel)
          ? getPricesOutputForBundleProduct()
          : getPricesOutputForNonBundleProduct()}
        {props.inStorePriceLabel && props.displayInStorePriceInsidePriceBlock && (
          <InStorePrice>
            {props.inStorePriceLabel}
          </InStorePrice>
        )}
      </PriceBlockWrapper>
    </>
  );

};

ProductPriceBlock.propTypes = {
  className: PropTypes.string,
  bundle: PropTypes.bool,
  priceDropped: PropTypes.bool,
  outOfStock: PropTypes.bool,
  currentPrice: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  savingsPrice: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  buyTogetherPrice: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  mainProductSaving: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  wasPrice: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  wasPriceFrom: PropTypes.string,
  wasPriceTo: PropTypes.string,
  mainProductCurrentPrice: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  mainProductWasPrice: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  mainProductWasPriceFrom: PropTypes.string,
  mainProductWasPriceTo: PropTypes.string,
  onPriceDropLinkClicked: PropTypes.func,
  priceDropLabel: PropTypes.string,
  priceDropLink: PropTypes.string,
  compact: PropTypes.bool,
  interaction: PropTypes.string,
  qa: PropTypes.string,
  isFlashSale: PropTypes.bool,
  webExclusivePriceTranslation: PropTypes.string,
  currency: PropTypes.string,
  perMonthPrice: PropTypes.oneOfType([
    PropTypes.string, PropTypes.number
  ]),
  perMonthTranslation: PropTypes.string,
  displayWasPrice: PropTypes.bool,
  displayWasPriceRange: PropTypes.bool,
  fromDateTranslation: PropTypes.string,
  toDateTranslation: PropTypes.string,
  displayBundlePriceInsidePriceBlock: PropTypes.bool,
  mainProductPriceLabel: PropTypes.string,
  separateSellingPriceLabel: PropTypes.string,
  inStorePriceLabel: PropTypes.string,
  displayInStorePriceInsidePriceBlock: PropTypes.bool,
  variant: PropTypes.string,
  storemode: PropTypes.bool
};

ProductPriceBlock.defaultProps = {
  className: undefined,
  bundle: false,
  priceDropped: false,
  outOfStock: false,
  currentPrice: undefined,
  savingsPrice: undefined,
  buyTogetherPrice: undefined,
  mainProductSaving: undefined,
  wasPrice: undefined,
  wasPriceFrom: undefined,
  wasPriceTo: undefined,
  mainProductCurrentPrice: undefined,
  mainProductWasPrice: undefined,
  mainProductWasPriceFrom: undefined,
  mainProductWasPriceTo: undefined,
  onPriceDropLinkClicked: () => {},
  priceDropLabel: 'Price drop',
  priceDropLink: undefined,
  compact: false,
  interaction: undefined,
  qa: undefined,
  isFlashSale: false,
  webExclusivePriceTranslation: undefined,
  currency: 'GBP',
  perMonthPrice: undefined,
  perMonthTranslation: 'per month',
  displayWasPrice: true,
  displayWasPriceRange: true,
  fromDateTranslation: undefined,
  toDateTranslation: undefined,
  displayBundlePriceInsidePriceBlock: false,
  mainProductPriceLabel: undefined,
  separateSellingPriceLabel: undefined,
  inStorePriceLabel: undefined,
  displayInStorePriceInsidePriceBlock: false,
  variant: undefined,
  storemode: false
};

export default ProductPriceBlock;
